import {
    Badge,
    BadgeType,
    CommunicationDirection,
    CommunicationType,
    DataTablePresenter,
    GuarantorCommunication,
    ITableColumn,
    normalizeCell,
    renderDateTime,
    renderEmpty,
    renderPhone,
    renderString,
    TimelineSegment,
} from '@enter/core';
import { mdiEmailOpenOutline, mdiEmailOutline, mdiEmailArrowRight } from '@mdi/js';
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import * as S from './details.styles';

interface IProps {
    communications: GuarantorCommunication[] | undefined;
}

export const Communications = ({ communications }: IProps) => {
    const { current: presenter } = React.useRef(
        new DataTablePresenter<GuarantorCommunication>({
            name: 'provider-guarantor-communications',
            data: communications || [],
            columns: [],
            filterSchemas: [],
        }),
    );

    const communicationsColumns: ITableColumn<GuarantorCommunication>[] = useMemo(
        () => [
            {
                id: 'segment',
                Cell: ({ cell }): React.ReactElement => (
                    <S.StyledSegment>
                        <TimelineSegment
                            start={cell.row.index === 0}
                            end={cell.row.index === _.size(presenter.data) - 1}
                        />
                    </S.StyledSegment>
                ),
                minWidth: 50,
                maxWidth: 50,
            },
            {
                id: 'Type',
                Header: 'Type',
                accessor: 'communicationType',
                maxWidth: 150,
                Cell: ({ cell }) => (
                    <S.Cell>
                        <Badge type={cell.value === CommunicationType.BILLING ? BadgeType.GREEN : BadgeType.BLUE_LIGHT}>
                            <S.BadgeBody
                                title={
                                    cell.row.original.communicationDirection === CommunicationDirection.INBOUND
                                        ? 'Inbound'
                                        : 'Outbound'
                                }>
                                {cell.row.original.communicationDirection === CommunicationDirection.INBOUND ? (
                                    <S.BadgeIcon path={mdiEmailOutline} size={0.6} />
                                ) : (
                                    <S.BadgeIcon path={mdiEmailArrowRight} size={0.6} />
                                )}
                                {normalizeCell(cell.value)}
                            </S.BadgeBody>
                        </Badge>
                    </S.Cell>
                ),
                disableSortBy: true,
            },
            {
                id: 'Contact',
                Header: 'Contact',
                Cell: ({ row }) => {
                    const phone = row.original.phone;
                    const email = row.original.email;

                    if (phone) {
                        return (
                            <S.Cell>
                                <S.AccountPhone href={`tel:${phone}`}>{renderPhone(phone)}</S.AccountPhone>
                            </S.Cell>
                        );
                    }

                    if (email) {
                        return (
                            <S.Cell>
                                <S.AccountEmail href={`mailto:${email}`}>
                                    <S.MailIcon path={mdiEmailOpenOutline} size={0.75} />
                                    {email}
                                </S.AccountEmail>
                            </S.Cell>
                        );
                    }

                    if (row.original.communicationMethod) {
                        return (
                            <S.Cell>
                                <Badge type={BadgeType.RED}>by {row.original.communicationMethod}</Badge>
                            </S.Cell>
                        );
                    }

                    return <S.Cell>{renderEmpty()}</S.Cell>;
                },
                disableSortBy: true,
            },
            {
                id: 'Message',
                Header: 'Recorded Message',
                accessor: 'message',
                Cell: ({ cell }) => <S.Cell>{renderString(cell.value)}</S.Cell>,
                disableSortBy: true,
                minWidth: 200,
            },
            {
                id: 'Date',
                Header: 'Date',
                accessor: 'communicationDate',
                Cell: ({ cell }) => <S.Cell>{renderDateTime(cell.value)}</S.Cell>,
                disableSortBy: true,
            },
        ],
        [presenter.data],
    );

    useEffect(() => {
        presenter.onColumnsUpdate(communicationsColumns);
    }, [presenter, communicationsColumns]);

    useEffect(() => {
        presenter.onDataUpdate(communications || []);
        presenter.onDataTotalChange(_.size(communications));
    }, [presenter, communications]);

    return (
        <S.StyledTitleCard size="large" info="Shows guarantor communications" title="Communications">
            <S.StyledDataTable
                presenter={presenter}
                isLoading={!communications}
                disableSorting={true}
                hideFiltering={true}
                hideNavigation={true}
            />
        </S.StyledTitleCard>
    );
};
